import { useGetMandates } from '@utils/api/mandates/use-get-mandates.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '@utils/capitalize.ts';
import { useUpdateCustomerMandate } from '@utils/api/mandates/use-update-mandates.tsx';
import { useOrganization } from '@utils/api/users/use-get-organization.tsx';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import { BaseError } from '@utils/types/shared/base.ts';
import { RotateCcw, Star } from 'lucide-react';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import type { GridAction } from '@components/atoms/datatable/grid-action-controller';

type MandateRow = {
  id: number;
  is_primary: string;
  raw_is_primary: boolean;
  details: string;
  status: string;
  method: string;
};

export const Payment = () => {
  const { t } = useTranslation();
  const { data: mandates, isLoading, isFetching } = useGetMandates();

  const { data: organization } = useOrganization();
  const { mutate: updateMandate, isLoading: isUpdating } =
    useUpdateCustomerMandate();

  const rows = useMemo<MandateRow[]>(() => {
    if (!mandates) return [];
    return mandates.result.map(m => ({
      id: m.id,
      is_primary: m.is_primary ? t('yes') : t('no'),
      raw_is_primary: m.is_primary,
      details: JSON.stringify(m.details),
      status: capitalizeFirstLetter(m.status),
      method: capitalizeFirstLetter(m.method)
    }));
  }, [mandates, t]);

  const columns = useMemo<DataGridColumn<MandateRow>[]>(
    () => [
      {
        id: 'is_primary',
        accessorKey: 'is_primary',
        header: t('primary-method'),
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'details',
        accessorKey: 'details',
        header: t('details'),
        minWidth: 240,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('status'),
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        id: 'method',
        accessorKey: 'method',
        header: t('method'),
        minWidth: 200,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ],
    [t]
  );

  const actions = useMemo<GridAction<MandateRow>[]>(
    () => [
      {
        id: 'make_primary',
        label: t('make-primary'),
        icon: <Star className="h-4 w-4" />,
        isDisabled: row => row?.raw_is_primary === true,
        run: row => {
          const toastId = toast.loading(t('updating-mandate'));
          updateMandate(
            {
              customerId: Number(organization?.id),
              mandateId: row.id,
              payload: { is_primary: true, revoke: false }
            },
            {
              onSuccess: () => {
                toast.success(t('mandate-updated-successfully'), {
                  id: toastId
                });
              },
              onError: (error: AxiosError<BaseError>) => {
                const fallback = t('an-error-occurred-while-updating-mandate');
                const data = error?.response?.data;
                toast.error(
                  data?.error_description ||
                    data?.detail ||
                    data?.error ||
                    data?.message ||
                    fallback,
                  { id: toastId }
                );
              }
            }
          );
        }
      },
      {
        id: 'revoke',
        label: t('revoke'),
        icon: <RotateCcw className="h-4 w-4" />,
        intent: 'danger',
        isDisabled: row => row?.raw_is_primary === true,
        run: row => {
          const toastId = toast.loading(t('updating-mandate'));
          updateMandate(
            {
              customerId: Number(organization?.id),
              mandateId: row.id,
              payload: { revoke: true, is_primary: false }
            },
            {
              onSuccess: () => {
                toast.success(t('mandate-updated-successfully'), {
                  id: toastId
                });
              },
              onError: (error: AxiosError<BaseError>) => {
                const fallback = t('an-error-occurred-while-updating-mandate');
                const data = error?.response?.data;
                toast.error(
                  data?.error_description ||
                    data?.detail ||
                    data?.error ||
                    data?.message ||
                    fallback,
                  { id: toastId }
                );
              }
            }
          );
        }
      }
    ],
    [t, updateMandate, organization]
  );

  return (
    <div className="py-4">
      <DataGridV2<MandateRow>
        data={rows}
        columns={columns}
        actions={actions}
        enableRowSelection={false}
        enablePagination
        manualPagination
        enableSorting={false}
        manualSorting={false}
        enableFiltering={false}
        manualFiltering={false}
        totalItems={mandates ? mandates.total_results : undefined}
        pageSize={25}
        pageSizeOptions={[25, 50, 75, 100]}
        showColumnFilters={false}
        filterMode="popover"
        height="82vh"
        dense
        grid={false}
        striped
        loading={isLoading || isFetching || isUpdating || !mandates}
        getRowId={row => String(row.id)}
      />
    </div>
  );
};
